import React, { useCallback, useState } from 'react';
import ConfirmActionModal from 'adapter/in/common/components/ConfirmActionModal/ConfirmActionModal';
import { dispatchLayoutsView, useLayoutsView } from 'adapter/in/common/context/Layouts/LayoutsContext';
import { useNavigation } from 'adapter/in/common/hooks/useNavigation';
import { buildReportAnalysisSearch, ReportAnalysisCoordinates } from 'adapter/in/common/analysis/reportAnalysisCoordinates';
import {
    SaveReportTemplateLayoutUseCase,
    useSaveReportTemplateLayout,
} from 'adapter/in/reports/hooks/useSaveReportTemplateLayout';
import gtm from 'adapter/in/reports/config/gtm';
import i18n from 'asset/translation';
import { reportsPath } from 'adapter/in/common/routes';
import { none } from 'fp-ts/lib/Option';

export const useNewAnalysisFromTemplateOverlayModal = (
    reportTemplateId: string,
    shouldConfirmUnsavedLayoutChanges: boolean,
    contextPeriod?: string,
    analysisCoordinates?: ReportAnalysisCoordinates,
    updateTemplateLayout?: SaveReportTemplateLayoutUseCase
) => {
    const [showConfirmNewAnalysisModal, setShowConfirmNewAnalysisModal] = useState(false);
    const { hasLayoutChanges } = useLayoutsView();
    const { setEditLayoutMode, setHasLayoutChanges } = dispatchLayoutsView();
    const { navigationPush } = useNavigation();

    const redirectToNewAnalysis = useCallback(() => {
        navigationPush(
            `template/${reportTemplateId}/analysis/new${buildReportAnalysisSearch(contextPeriod, analysisCoordinates)}`,
            reportsPath
        );
    }, [analysisCoordinates, contextPeriod, navigationPush, reportTemplateId]);

    const { handleUpdateLayoutAction, isSaving, hasError } = useSaveReportTemplateLayout(
        reportTemplateId,
        updateTemplateLayout ?? {
            dataFromUpdateTemplateLayout: none,
            execute: () => undefined,
            reset: () => undefined,
            error: none,
            isLoading: false,
        },
        {
            onSaveSuccess: redirectToNewAnalysis,
        }
    );

    const handleOnConfirmModal = () => {
        if (!shouldConfirmUnsavedLayoutChanges || !hasLayoutChanges || !updateTemplateLayout) {
            redirectToNewAnalysis();
            return;
        }

        setShowConfirmNewAnalysisModal(true);
    };

    const handleRedirectWithoutSaving = () => {
        setShowConfirmNewAnalysisModal(false);
        setHasLayoutChanges(false);
        setEditLayoutMode(false);
        redirectToNewAnalysis();
    };

    const onConfirmModal = (
        <ConfirmActionModal
            title={i18n.t('reports.reportTemplate.newAnalysisOverlayModal.title')}
            isLoading={isSaving}
            hasError={hasError}
            isOpen={showConfirmNewAnalysisModal}
            onClose={() => setShowConfirmNewAnalysisModal(false)}
            mainButton={handleUpdateLayoutAction}
            mainButtonText={i18n.t('reports.reportTemplate.newAnalysisOverlayModal.saveAndConfirm')}
            mainButtonVariant='primary'
            optionalButton={handleRedirectWithoutSaving}
            optionalButtonText={i18n.t('reports.reportTemplate.newAnalysisOverlayModal.confirm')}
            optionalButtonVariant='destructive'
            message={i18n.t('reports.reportTemplate.newAnalysisOverlayModal.message')}
            dataIdContext={gtm.newAnalysisFromTemplateOverlayModal}
        />
    );

    return { handleOnConfirmModal, onConfirmModal };
};
